<script>
	import { onMount } from 'svelte';
	import { Button } from '$lib/components/ui/button';
	import { handleAnchorClick } from '$lib/components/golf/scroll';
	import { LEGENDS_PRICE, legendsFacts } from '$lib/data/legends';
	import heroImage from '$lib/assets/golf/ratho-farm-golfer.jpg?enhanced';

	let animate = $state(false);

	onMount(() => {
		setTimeout(() => {
			window.scrollTo(0, 0);
		}, 200);
		animate = true;
	});

	const facts = legendsFacts;
</script>

<section class="relative flex min-h-[720px] flex-col justify-center lg:min-h-[800px]">
	<enhanced:img
		src={heroImage}
		alt="A golfer teeing off beside a historic stone hut at Ratho Farm"
		class="absolute inset-0 h-full w-full object-cover"
		sizes="100vw"
		fetchpriority="high"
	/>
	<!-- Two layers: a flat wash for legibility, plus a bottom gradient so the
	     facts bar reads as part of the image rather than a pasted-on strip. -->
	<div class="absolute inset-0 bg-[#0d211d]/45"></div>
	<div
		class="absolute inset-0 bg-gradient-to-t from-[#0d211d] via-[#0d211d]/20 to-transparent"
	></div>

	<div
		class={`relative z-10 mx-auto flex w-full max-w-4xl flex-col items-center px-4 pb-52 pt-28 text-center lg:pb-44 ${
			animate ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
		}`}
		style="transition: all 0.9s cubic-bezier(0.17, 0.55, 0.55, 1) 0.4s"
	>
		<p class="text-xs font-semibold uppercase tracking-[0.4em] text-[#ffd451] sm:text-sm">
			Golf • History • Flavour
		</p>
		<h1
			class="mt-5 font-heading text-4xl font-bold leading-[1.05] tracking-tight text-background sm:text-6xl lg:text-7xl"
		>
			Legends of Tasmania
		</h1>
		<p class="mt-5 font-heading text-xl font-semibold text-background/95 sm:text-2xl lg:text-3xl">
			More than just a golf tour
		</p>
		<p class="mx-auto mt-5 max-w-2xl leading-7 text-background/85 lg:text-lg">
			World-class golf. Rich Tasmanian history. Great stories. Plenty of laughs.
		</p>

		<div class="mt-8 flex items-baseline gap-2 text-background">
			<span class="text-sm font-medium text-background/70">From AUD</span>
			<span class="font-heading text-3xl font-bold lg:text-4xl">${LEGENDS_PRICE}pp</span>
			<span class="text-sm font-medium text-background/70">twin share</span>
		</div>

		<div class="mt-8 flex w-full flex-col items-center justify-center gap-4 sm:w-auto sm:flex-row">
			<Button
				data-sveltekit-noscroll
				onclick={handleAnchorClick}
				href="#book"
				class="w-full rounded-full bg-tt-red px-8 font-semibold hover:bg-tt-red/90 sm:w-auto lg:text-lg"
				>Check dates & book</Button
			>
			<Button
				data-sveltekit-noscroll
				onclick={handleAnchorClick}
				href="#itinerary"
				variant="outline"
				class="w-full rounded-full border-2 bg-transparent px-8 font-semibold text-background hover:bg-background hover:text-foreground sm:w-auto lg:text-lg"
				>See the itinerary</Button
			>
		</div>
	</div>

	<!-- At-a-glance bar, pinned to the base of the hero. The extra bottom padding
	     keeps its text clear of the angled edge below. -->
	<div class="absolute inset-x-0 bottom-0 z-10 border-t border-white/15 backdrop-blur-sm">
		<dl
			class="mx-auto grid max-w-7xl grid-cols-2 gap-y-6 px-4 pb-14 pt-6 text-background lg:grid-cols-4 lg:pb-20 lg:pt-7"
		>
			{#each facts as fact}
				<div class="lg:border-l lg:border-white/15 lg:pl-6 lg:first:border-l-0 lg:first:pl-0">
					<dt class="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#ffd451]">
						{fact.label}
					</dt>
					<dd class="mt-1.5 font-heading text-sm font-semibold sm:text-base">{fact.value}</dd>
				</div>
			{/each}
		</dl>
	</div>

	<!-- Angled base, the same shape used on the private golf experiences hero.
	     Sits above the facts bar so it trims the whole hero, image and all. -->
	<div
		class="absolute bottom-0 left-0 z-20 h-48 w-full"
		style="clip-path: polygon(26% 96%,100% 73%,100% 100%,0 100%,0 88%);"
	>
		<div class="h-full bg-white"></div>
	</div>
</section>
